import { createFileRoute, redirect, useParams } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { CourseProductPicker } from "@/pages/Courses/CourseProductPicker";
import { kursyAll } from "@/kursy/general";
import { getSubscriptionCourseOptions } from "@/api/queryOptions";
import { queryClient } from "@/main";
import type { CourseKey } from "@/api/api.types";

const CourseIndex = (): JSX.Element => {
	const { courseKey } = useParams({ from: "/(app)/_app/course/$courseKey/" });
	const { data: subscription } = useSuspenseQuery(
		getSubscriptionCourseOptions(courseKey as CourseKey)
	);
	const products = subscription?.products ?? [];
	const parentKurs = kursyAll.find((kurs) => kurs.slug === courseKey);
	return (
		<CourseProductPicker
			courseKey={courseKey as CourseKey}
			parentKurs={parentKurs}
			products={products}
		/>
	);
};

export const Route = createFileRoute("/(app)/_app/course/$courseKey/")({
	component: CourseIndex,
	beforeLoad: async ({ params }) => {
		// Cache-first: parent layout warms the cache via `ensureQueryData`, so
		// `getQueryData` returns it synchronously and we skip the HTTP round-trip.
		// `ensureQueryData` is the fallback for the rare case the parent has not run yet.
		const options = getSubscriptionCourseOptions(params.courseKey as CourseKey);
		const cached = queryClient.getQueryData(options.queryKey);
		const data = cached ?? (await queryClient.ensureQueryData(options));
		if (!data || !data.has_access) {
			// Parent layout will render the access error; do not redirect here.
			return;
		}
		if (data.products.length === 1) {
			const product = data.products[0];
			if (product) {
				redirect({
					to: "/course/$courseKey/$productId",
					params: {
						courseKey: params.courseKey,
						productId: String(product.id),
					},
					throw: true,
					replace: true,
				});
			}
		}
	},
});
